import { useMemo, useState } from "react";
import { Check } from "lucide-react";

const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
const shuffle = (arr, seed) => arr.map((v, i) => [v, Math.sin(seed * 9301 + i * 49297) % 1]).sort((a, b) => a[1] - b[1]).map((x) => x[0]);

export const answerText = (q) => {
  if (q.type === "matching") return (q.pairs || []).map((p) => `${p.left} → ${p.right}`).join("; ");
  if (q.type === "fill" || q.type === "dnd") return (q.answers || []).map((a) => a.split("|")[0]).join(", ");
  return q.options?.[q.answer];
};

const CheckBtn = ({ onClick, disabled }) => (
  <button onClick={onClick} disabled={disabled} data-testid="quiz-check-btn" className="neu-btn-primary mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-bold disabled:opacity-50"><Check className="h-4 w-4" />Check answer</button>
);

const MatchingQ = ({ q, locked, onCheck }) => {
  const rights = useMemo(() => shuffle((q.pairs || []).map((p) => p.right), q.pairs?.length || 1), [q]);
  const [sel, setSel] = useState({});
  const done = (q.pairs || []).every((_, i) => sel[i]);
  return (
    <div data-testid="quiz-matching">
      <div className="mt-5 space-y-2.5">
        {(q.pairs || []).map((p, i) => {
          const ok = locked && sel[i] === p.right;
          return (
            <div key={i} className="grid grid-cols-2 items-center gap-3">
              <span className="neu-flat rounded-2xl px-4 py-2.5 text-sm font-semibold">{p.left}</span>
              <select disabled={locked} value={sel[i] || ""} onChange={(e) => setSel({ ...sel, [i]: e.target.value })} data-testid={`quiz-match-select-${i}`}
                className={`rounded-2xl px-3 py-2.5 text-sm focus:outline-none ${locked ? (ok ? "bg-[#5FA82E] text-white" : "bg-[#A62B8A] text-white") : "neu-inset"}`}>
                <option value="">Choose…</option>
                {rights.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          );
        })}
      </div>
      {!locked && <CheckBtn disabled={!done} onClick={() => onCheck(q.pairs.every((p, i) => sel[i] === p.right))} />}
    </div>
  );
};

const Blanks = ({ prompt, render }) => {
  const parts = (prompt || "").split("___");
  return <p className="mt-5 text-lg leading-[2.6]">{parts.map((t, i) => <span key={i}>{t}{i < parts.length - 1 && render(i)}</span>)}</p>;
};

const isRight = (q, i, v) => (q.answers?.[i] || "").split("|").some((a) => norm(a) === norm(v));

const FillQ = ({ q, locked, onCheck }) => {
  const [vals, setVals] = useState({});
  const n = (q.prompt || "").split("___").length - 1;
  return (
    <div data-testid="quiz-fill">
      <Blanks prompt={q.prompt} render={(i) => (
        <input value={vals[i] || ""} disabled={locked} onChange={(e) => setVals({ ...vals, [i]: e.target.value })} data-testid={`quiz-fill-input-${i}`}
          className={`mx-1 inline-block w-36 rounded-xl px-3 py-1 text-center text-base font-semibold focus:outline-none ${locked ? (isRight(q, i, vals[i]) ? "bg-[#5FA82E] text-white" : "bg-[#A62B8A] text-white") : "neu-inset"}`} />
      )} />
      {!locked && <CheckBtn disabled={Object.values(vals).filter((v) => v?.trim()).length < n} onClick={() => onCheck([...Array(n)].every((_, i) => isRight(q, i, vals[i])))} />}
    </div>
  );
};

const DndQ = ({ q, locked, onCheck }) => {
  const bank = useMemo(() => shuffle([...(q.answers || []).map((a) => a.split("|")[0]), ...(q.distractors || [])], (q.answers?.length || 1) + 3), [q]);
  const n = (q.prompt || "").split("___").length - 1;
  const [slots, setSlots] = useState({});
  const [held, setHeld] = useState(null);
  const used = Object.values(slots);
  const place = (i, word) => { if (locked || !word) return; const s = { ...slots }; Object.keys(s).forEach((k) => s[k] === word && delete s[k]); s[i] = word; setSlots(s); setHeld(null); };
  return (
    <div data-testid="quiz-dnd">
      <Blanks prompt={q.prompt} render={(i) => (
        <span onDragOver={(e) => e.preventDefault()} onDrop={(e) => place(i, e.dataTransfer.getData("text/plain"))} onClick={() => (held ? place(i, held) : !locked && setSlots(({ [i]: _, ...r }) => r))}
          data-testid={`quiz-dnd-slot-${i}`}
          className={`mx-1 inline-flex min-w-[7rem] cursor-pointer justify-center rounded-xl border-2 border-dashed px-3 py-0.5 align-middle text-base font-semibold ${locked ? (isRight(q, i, slots[i]) ? "border-transparent bg-[#5FA82E] text-white" : "border-transparent bg-[#A62B8A] text-white") : slots[i] ? "border-[#3D3DF0] bg-white" : "border-[#3D3DF0]/40"}`}>
          {slots[i] || "drop here"}
        </span>
      )} />
      <div className="mt-4 flex flex-wrap gap-2">
        {bank.filter((w) => !used.includes(w)).map((w) => (
          <span key={w} draggable={!locked} onDragStart={(e) => e.dataTransfer.setData("text/plain", w)} onClick={() => !locked && setHeld(held === w ? null : w)} data-testid={`quiz-dnd-chip-${w}`}
            className={`cursor-grab select-none rounded-full px-4 py-2 text-sm font-semibold ${held === w ? "bg-[#3D3DF0] text-white" : "neu-btn"}`}>{w}</span>
        ))}
      </div>
      {!locked && <p className="mt-2 text-[11px] text-[#64748B]">Drag a word into a blank — or tap a word, then tap a blank.</p>}
      {!locked && <CheckBtn disabled={used.length < n} onClick={() => onCheck([...Array(n)].every((_, i) => isRight(q, i, slots[i])))} />}
    </div>
  );
};

export const AdvancedQuestion = ({ q, locked, onCheck }) => {
  if (q.type === "matching") return <MatchingQ q={q} locked={locked} onCheck={onCheck} />;
  if (q.type === "fill") return <FillQ q={q} locked={locked} onCheck={onCheck} />;
  return <DndQ q={q} locked={locked} onCheck={onCheck} />;
};
